import React, { useState, useEffect, useImperativeHandle, useRef } from "react";
import RadioButtonGroup from "../../elements/radio-button-group";
import { generateReactKey } from "../../utils/reactKey";
import Icon from "../../elements/icon";
import Heading from "../../elements/heading";
import api from "../../utils/api";
import { connect } from "react-redux";
import { selectUserState } from "../../selectors/application";
import { shippingInfoSelector } from "../../selectors/gears-checkout";
import AccountRemoveAddressModal from "../account-remove-address-modal";
import BillingInfoForm from "../billing-info-form";
import "./style.scss";
import PreSalesBillingInfoForm from "../presales-billing-info-form";
import { getMarketCountry, isLoggedIn } from "../../services/siteService";
import { SuperButton } from "../../elements";
import AddressInformation from "../address-information";
const PreSalesBillingInfo = React.forwardRef(({ fields, user, addressList, noAddress, loadAddressList, addAddress, updateAddress, removeAddress, ...otherFields }, ref) => {
  const { headingType, heading, addressUnSavedMessage, addressMissingMessage, removeAddressModal, ctaNameChange, ctaTypeChange, colorSchemeChange, iconNameChange, countryPrivateImportMessage, shippingFormData } = fields;
  const componentRoot = useRef();
  const addAddressRef = useRef();
  const editAddressRef = useRef(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [marketCountry, setMarketCountry] = useState(null);
  const [showRemoveAddressModal, setShowRemoveAddressModal] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [addressEditing, setAddressEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  useImperativeHandle(ref, () => ({
    validateForm() {
      if (isLoggedIn() && addressList.length > 0) {
        if (editAddress) {
          editAddressRef.current.setError(addressUnSavedMessage.value);
          editAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        } else if (selectedAddress) {
          if (selectedAddress.address) {
            return {
              isValid: true,
              formData: {
                ...selectedAddress.address,
                email: user?.email,
                phoneArea: user?.mobileNoCountryCode,
                phone: user?.mobileNo
              }
            };
          } else {
            addAddressRef.current.setError(addressUnSavedMessage.value);
            addAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
            return {
              isValid: false,
              formData: null
            };
          }
        } else {
          setFormError(addressMissingMessage.value);
          componentRoot.current.scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        }
      } else {
        return addAddressRef.current.validateForm();
      }
    }
  }));
  useEffect(() => {
    getMarketCountry().then(country => {
      setMarketCountry(country);
    });
  }, []);
  useEffect(() => {
    if (addAddressRef.current) {
      setDefaultAddress({
        firstName: shippingFormData?.firstName || user?.firstName || "",
        lastName: shippingFormData?.lastName || user?.lastName || "",
        street: shippingFormData?.street || user?.street || "",
        apartment: shippingFormData?.apartment || user?.apartment || "",
        postalCode: shippingFormData?.postalCode || user?.postalCode || "",
        city: shippingFormData?.city || user?.city || ""
      });
    }
  }, [user, shippingFormData, addAddressRef]);
  useEffect(() => {
    if (addressList.length > 0) {
      let _selectedAddress = null;
      if (shippingFormData?.addressId) {
        _selectedAddress = addressList.find(x => x.value === shippingFormData?.addressId);
      } else {
        _selectedAddress = addressList.find(x => x.isNew);
        if (!_selectedAddress) {
          _selectedAddress = addressList.find(x => x.address?.defaultFlag == "1");
        }
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList[0];
      }
      setSelectedAddress(_selectedAddress);
    }
  }, [addressList, shippingFormData]);
  const onAddressChange = address => {
    if (selectedAddress !== address) {
      setSelectedAddress(address);
      if (address.value !== "new") {
        setEditAddress(null);
        setAddressEditing(false);
      } else {
        setDefaultAddress({
          firstName: user?.firstName || "",
          lastName: user?.lastName || ""
        });
      }
    }
  };
  const formatSavedAddress = address => {
    let name = `${address?.firstName || ""} ${address?.lastName || ""}`;
    let street = `${address?.street || ""} ${address?.apartment || ""}`;
    let zipCity = `${address?.postalCode || ""} ${address?.city || ""}`;
    let country = `${address?.country || ""}`;
    return [name, street, zipCity, country];
  };
  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showRemoveAddressModal,
    onModalClose: evt => {
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: addressToRemove.value
      });
      removeAddress(addressToRemove);
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    }
  };
  const onEditClick = (evt, option) => {
    evt.stopPropagation();
    setEditAddress(option);
    setSelectedAddress(option);
  };
  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    setAddressToRemove(option);
    setShowRemoveAddressModal(true);
  };
  const addAddressFormProps = {
    fields,
    ref: addAddressRef,
    address: defaultAddress,
    savable: addressList.length > 0,
    async onSaveSuccess(address) {
      setEditAddress(null);
      await addAddress(address);
      setAddressEditing(false);
    }
  };
  const ctaChangeAddressProps = ctaNameChange.value && {
    buttonText: ctaNameChange.value,
    ctaType: ctaTypeChange,
    colorScheme: colorSchemeChange,
    iconName: iconNameChange,
    callback(evt) {
      setAddressEditing(true);
    }
  };
  const addressLayout = option => {
    if (option.address) {
      if (editAddress === option) {
        const editAddressFormProps = {
          fields,
          ref: editAddressRef,
          address: option.address,
          onSaveSuccess() {
            setSelectedAddress(editAddress);
            updateAddress();
            setEditAddress(null);
            setAddressEditing(false);
          }
        };
        return (
          <div className="edit-address-block" key={generateReactKey()} onClick={e => e.stopPropagation()}>
            <p className="label">Edit Address</p>
            <BillingInfoForm {...editAddressFormProps} />
          </div>
        );
      } else {
        return (
          <div className="saved-address-block" key={generateReactKey()}>
            <AddressInformation
              className="body-container"
              {...{
                address: option.address,
                marketCountry,
                countryPrivateImportMessage: selectedAddress === option ? countryPrivateImportMessage : null
              }}
            />
            <div className="icons">
              <Icon name="Edit" colorScheme="outline-green" className="icon --visible" onClick={evt => onEditClick(evt, option)} />
              {selectedAddress !== option && addressList.length > 2 && option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}
            </div>
          </div>
        );
      }
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  return (
    <div className="billing-info" ref={componentRoot}>
      <div className="title">
        <Heading type={headingType} html={heading} />
      </div>
      {!addressEditing && selectedAddress && (
        <AddressInformation
          className="default-address"
          {...{
            address: selectedAddress.address,
            marketCountry,
            countryPrivateImportMessage
          }}
        />
      )}
      {addressEditing && isLoggedIn() && <RadioButtonGroup options={addressList} html={addressLayout} alignment="top" onChange={onAddressChange} selectedOption={selectedAddress} />}
      {(!isLoggedIn() || (selectedAddress && selectedAddress.value === "new") || noAddress) && <PreSalesBillingInfoForm {...addAddressFormProps} />}
      {!addressEditing && addressList.length > 0 && (
        <div className="cta-edit-address">
          <SuperButton {...ctaChangeAddressProps} />
        </div>
      )}
      {formError && <div className="failed-message">{formError}</div>}
      <AccountRemoveAddressModal {...removeAddressModalProps} />
    </div>
  );
});
PreSalesBillingInfo.displayName = "BillingInfo";
const mapStateToProps = state => ({
  user: selectUserState(state),
  shippingFormData: shippingInfoSelector(state)
});
export default connect(mapStateToProps, null, null, { forwardRef: true })(PreSalesBillingInfo);
